import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiClient from "../../hooks/useApiClient";
import RealAssetDeploymentTreeDropdown from "../realAsset/realAssetDeploymentTreeDropdown";

const DeploymentGrantsManagement = ({ organizationId, initialGrants }: { organizationId: string; initialGrants: string[] }) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [selectedDeployments, setSelectedDeployments] = useState(initialGrants);

  useEffect(() => {
    setSelectedDeployments(initialGrants);
  }, [initialGrants]);

  const addGrantsMutation = useMutation({
    mutationFn: (deploymentIds: string[]) =>
      api.users.bulkAddDeploymentGrantsForOrganization({
        organizationId,
        requestBody: deploymentIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", organizationId] });
    },
  });

  const removeGrantsMutation = useMutation({
    mutationFn: (deploymentIds: string[]) =>
      api.users.bulkDeleteDeploymentGrantsForOrganization({
        organizationId,
        requestBody: deploymentIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", organizationId] });
    },
  });

  const handleDeploymentChange = (changedDeploymentIds: string[]) => {
    const deploymentsToAdd: string[] = [];
    const deploymentsToRemove: string[] = [];
    for (const id of changedDeploymentIds) {
      if (!selectedDeployments.includes(id)) {
        deploymentsToAdd.push(id);
      } else {
        deploymentsToRemove.push(id);
      }
    }
    setSelectedDeployments((prev: string[]) => [
      ...prev.filter((i: string) => !deploymentsToRemove.includes(i)),
      ...deploymentsToAdd,
    ]);
    return Promise.all([
      addGrantsMutation.mutateAsync(deploymentsToAdd),
      removeGrantsMutation.mutateAsync(deploymentsToRemove),
    ]);
  };

  const isLoading =
    addGrantsMutation.isPending || removeGrantsMutation.isPending;
  const isError = addGrantsMutation.isError || removeGrantsMutation.isError;

  return (
    <div>
      <h2 className="mb-4 text-2xl font-semibold text-blue-200">
        Deployment Grants
      </h2>
      <div className="mb-2 flex items-center space-x-4">
        <div className="relative z-50 flex-grow">
          <RealAssetDeploymentTreeDropdown
            selectedIds={selectedDeployments}
            onOptionToggle={handleDeploymentChange}
            showHideUnselectedToggle={true}
          />
        </div>
        <div className="flex flex-col items-end">
          <span className="text-3xl font-bold text-blue-200">
            {selectedDeployments.length}
          </span>
          <span className="text-sm text-gray-500">Granted Deployments</span>
        </div>
      </div>
      <div className="h-6">
        {" "}
        {/* Fixed height area for status messages */}
        <p
          className={`text-sm transition-opacity duration-300 ${
            isLoading ? "opacity-100" : "opacity-0"
          }`}
        >
          Updating grants...
        </p>
        <p
          className={`text-sm text-red-500 transition-opacity duration-300 ${
            isError ? "opacity-100" : "opacity-0"
          }`}
        >
          Error updating grants
        </p>
      </div>
    </div>
  );
};

export default DeploymentGrantsManagement;
